import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Collapse,
  IconButton,
  Typography,
  Card,
  CardContent,
  Box,
  useMediaQuery,
  useTheme,
  Popover,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@material-ui/icons/KeyboardArrowUp";
import { FilterFilled, LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Pagination as AntPagination } from "antd";
import { commonStrings } from "./StringBundle";
import { useGlobalStyles } from "./globalStyles";
import FilterChip from "./FilterChip";
import FilterPopup from "./FilterPopup";
import Loading from "./Order/Loading";
import _, { unset } from "lodash";
import { CheckBox } from "./CheckBox";
import { Pagination } from "@material-ui/lab";
import { colors } from "../constants/colors";
import CustomPagination from "./CustomPagination";

const useStyles = makeStyles((theme) => ({
  root: {
    width: "100%",
    margin: "0 auto",
    maxHeight: "60vh",
    overflowY: "auto",
  },
  // Wrapper div controls the 95% width + float — avoids conflict with MUI TableContainer's width:100%
  nestedWrapper: {
    width: "95%",
    float: "right",
    margin: "0 10px 25px",
  },
  nestedRoot: {
    maxHeight: "30vh",
    overflowY: "auto",
    boxShadow:
      "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)",
  },
  table: { minWidth: "95%", backgroundColor: "#fff" },
  header: {
    backgroundColor: "#feecda",
    position: "sticky",
    top: 0,
    zIndex: 3,
    '& .MuiTableCell-head': {
          padding: '5px 16px !important'
    }
  },
  subHeader: {
    backgroundColor: "#feecda",
    position: "sticky",
    top: 0,
    zIndex: 2,
  },
  mainRowRow: { backgroundColor: "#fff" },
  childRow: { backgroundColor: "#fff" },
  childrenRowCell: { padding: "10px 16px" },
  mainRowCell: { padding: "10px 16px" },
  collaspseRowCell: { 
    padding: "0 4px",
    '& .MuiFormControlLabel-root': {
      margin: 0
    },
  },
  arrowScrollWrap: { position: "relative" },
  subHeaderCellCentered: { textAlign: "center !important" },
  unboundedRoot: {
    maxHeight: "none !important",
    overflowY: "visible !important",
  },
  unboundedNestedRoot: {
    maxHeight: "none !important",
    overflowY: "visible !important",
  },
  noHorizontalScrollbar: {
    msOverflowStyle: "none",
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
  noData: { textAlign: "center", padding: "20px", color: "#777" },
  emptyStateCell: { border: "none", padding: "40px 20px" },
  emptyStateBox: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing(4),
    gap: theme.spacing(1),
  },
  emptyStateText: {
    color: "#999",
    fontSize: "0.95rem",
    fontWeight: 500,
  },
  subHeaderCell: { paddingBottom: 0, paddingTop: 0 },
  subHeaderTitle: {
    marginTop: '10px',
    marginBottom: '10px',
    paddingLeft: '4%',
  },
  paginationContainer: {
    display: "flex",
    justifyContent: "flex-end",  
    marginTop: theme.spacing(2),
  },
  pagination: {
    "& .MuiPaginationItem-root": {
      border: "1px solid #ccc",  
      borderRadius: "unset",
      margin: "0 2px",
    },
    "& .Mui-selected": {
      backgroundColor: "transparent", 
      color: colors.orange,
      borderColor: colors.orange,
    },
    "& .MuiPaginationItem-root:hover": {
      backgroundColor: "transparent",
      color: colors.link,
      borderColor: colors.link,
    },
    "& .MuiPaginationItem-ellipsis": {
      height: "32px",
    }
  },
  pagelimitoptions: {
    "& .MuiOutlinedInput-root": {
      height: '32px',
      borderRadius: 'unset',
      marginLeft: '15px',
    }
  },
  // Mobile Card Styles
  mobileContainer: {
    width: "100%",
    padding: theme.spacing(1),
  },
  mobileCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    overflow: "hidden",
  },
  mobileCardHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    backgroundColor: "#feecda",
    padding: theme.spacing(1, 2),
  },
  mobileCardContent: {
    padding: theme.spacing(2),
  },
  mobileCardRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    padding: theme.spacing(0.75, 0),
    borderBottom: "1px solid #f0f0f0",
    "&:last-child": {
      borderBottom: "none",
    },
  },
  mobileCardLabel: {
    fontWeight: 600,
    color: "#666",
    fontSize: "0.85rem",
    flex: "0 0 40%",
  },
  mobileCardValue: {
    color: "#333",
    fontSize: "0.9rem",
    textAlign: "right",
    flex: "0 0 58%",
    wordBreak: "break-word",
  },
  mobileChildContainer: {
    backgroundColor: "#fafafa",
    margin: theme.spacing(0, 1, 1, 1),
    borderRadius: 4,
    padding: theme.spacing(1),
  },
  mobileChildCard: {
    backgroundColor: "#fff",
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1.5),
    borderRadius: 4,
    boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.08)",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  mobileChildTitle: {
    fontSize: "0.9rem",
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: "#333",
  },
  mobileExpandBtn: {
    padding: 4,
  },
  mobileCheckboxContainer: {
    display: "flex",
    alignItems: "center",
  },
  mobileNoDataCard: {
    marginBottom: theme.spacing(2),
    borderRadius: 8,
    boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  mobileNoData: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: "#777",
  },
  mobileLoading: {
    display: "flex",
    justifyContent: "center",
    padding: theme.spacing(4),
  },
 }));

// Per-column filter icon + popover — delegates popup UI to the shared FilterPopup component
function ColumnFilterPopover({ col, activeValue, onApply, onRemove }) {
  const globalClasses = useGlobalStyles();
  const [anchorEl, setAnchorEl] = useState(null);

  const isActive = Boolean(activeValue);

  const handleOpen = (e) => {
    e.stopPropagation();
    setAnchorEl(e.currentTarget);
  };
  const handleClose = () => setAnchorEl(null);

  return (
    <>
      <IconButton size="small" className={globalClasses.filterIconBtn} onClick={handleOpen}>
        <FilterFilled className={isActive ? globalClasses.filterIconActive : globalClasses.filterIconDefault} />
      </IconButton>
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        onClick={(e) => e.stopPropagation()}
        disableRestoreFocus
      >
        <Box className={globalClasses.filterPopoverContent}>
          <FilterPopup
            label={col.headerName}
            activeValue={activeValue}
            onApply={(value) => { handleClose(); onApply(col, value); }}
            onRemove={() => { handleClose(); onRemove(col.field); }}
            onClose={handleClose}
          />
        </Box>
      </Popover>
    </>
  );
}

// Mobile Child Card Component
function MobileChildCard({ subRowCols, childData }) {
  const classes = useStyles();
  
  return (
    <Box className={classes.mobileChildCard}>
      {subRowCols.map((col, idx) => (
        <Box key={idx} className={classes.mobileCardRow}>
          <Typography className={classes.mobileCardLabel}>
            {col.headerName}
          </Typography>
          <Typography className={classes.mobileCardValue}>
            {col.render ? col.render(childData) : (childData[col.field] ?? "-")}
          </Typography>
        </Box>
      ))}
    </Box>
  );
}

function MobileChildrenRow({ data, mainRowCols, subRowCols, subHeaderTitle, childDataKey, childApiCall, getChildData, mode, identifier_key, selected, setSelected, headerkey, onOpenClose, hasChildren, loadingChild, onCheckboxClick, open }){
  const classes = useStyles();
  const globalClasses = useGlobalStyles();
    return(
      <Card className={classes.mobileCard}>
        {/* Card Header with checkbox and expand button */}
        <Box className={classes.mobileCardHeader}>
          <Box className={classes.mobileCheckboxContainer}>
            {!_.isEmpty(mode) && (
              <CheckBox
                checked={data[identifier_key] === selected}
                onChange={onCheckboxClick}
              />
            )}
            {hasChildren && (
              <IconButton
                size="small"
                onClick={onOpenClose}
                className={classes.mobileExpandBtn}
              >
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
            <Typography className={globalClasses.ml5}>{data?.[headerkey]}</Typography>
          </Box>
        </Box>

        {/* Card Content - Main Row Data */}
        <CardContent className={classes.mobileCardContent}>
          {mainRowCols.map((col, idx) => {
            if(col.field === headerkey) return;
            return(
              <Box key={idx} className={classes.mobileCardRow}>
                <Typography className={classes.mobileCardLabel}>
                  {col.headerName}
                </Typography>
                <Typography className={classes.mobileCardValue}>
                  {col.render ? col.render(data) : (data[col.field] ?? "-")}
                </Typography>
              </Box>
            )
          })}
        </CardContent>

        {/* Collapsible Child Data */}
        {hasChildren && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box className={classes.mobileChildContainer}>
              <Box className={classes.mobileCardHeader}>
          <Box className={classes.mobileCheckboxContainer}>
              {subHeaderTitle && (
                <Typography className={classes.mobileChildTitle}>
                  {subHeaderTitle}
                </Typography>
              )}
              </Box>
              </Box>
              {loadingChild && !data?.[childDataKey] ? (
                <Box className={classes.mobileLoading}>
                  <Loading />
                </Box>
              ) : data?.[childDataKey]?.length === 0 ? (
                <Typography className={classes.mobileNoData}>
                  {commonStrings.noRecords}
                </Typography>
              ) : (
                data?.[childDataKey]?.map((sub, i) => (
                  <MobileChildCard
                    key={i}
                    subRowCols={subRowCols}
                    childData={sub}
                  />
                ))
              )}
            </Box>
          </Collapse>
        )}
      </Card>
    );
}

//  child table component (Desktop)
 function ChildTable({
   mode,
   subHeaderTitle,
   subRowCols,
   data,
   childDataKey,
   mainRowCols,
   open,
   loadingChild,
   setLoadingChild,
   centerSubHeaders,
   unboundedNestedHeight,
 }) {
   const classes = useStyles();
   const [showError, setShowError] = useState("");

   React.useEffect(() => {
     const childData = data?.[childDataKey];
     if (childData !== undefined) {
       if (childData === null || childData.length === 0) {
         setShowError(commonStrings.noRecords);
       } else {
         setShowError("");
       }

       setLoadingChild(false);
     } else {
       setShowError(commonStrings.serverError);
       setLoadingChild(false);
     }
   }, [data[childDataKey]]);

   return (
     <TableRow>
       <TableCell
         className={classes.subHeaderCell}
         colSpan={
           _.isEmpty(mode) ? mainRowCols.length + 1 : mainRowCols.length + 2
         }
       >
         <Collapse in={open} timeout="auto" unmountOnExit>
           {subHeaderTitle && (
             <Typography variant="h6" className={classes.subHeaderTitle}>
               {subHeaderTitle}
             </Typography>
           )}
         <Box className={classes.nestedWrapper}>
           <TableContainer component={Paper} className={unboundedNestedHeight ? `${classes.nestedRoot} ${classes.unboundedNestedRoot}` : classes.nestedRoot}>
             <Table size="small">
               <TableHead className={classes.subHeader}>
                 <TableRow>
                   {subRowCols.map((col, idx) => (
                     <TableCell
                       key={idx}
                       align={col.align || "center"}
                       className={centerSubHeaders ? classes.subHeaderCellCentered : undefined}
                     >
                       {col.headerName}
                     </TableCell>
                   ))}
                 </TableRow>
               </TableHead>
               {loadingChild && !data?.[childDataKey] ? (
                 <TableRow>
                   <TableCell
                     colSpan={subRowCols.length}
                     className={classes.noData}
                   >
                     <Loading />
                   </TableCell>
                 </TableRow>
               ) : (
                 <TableBody>
                   {showError ? (
                     <TableRow>
                       <TableCell
                         colSpan={subRowCols.length}
                         className={classes.noData}
                       >
                         {showError}
                       </TableCell>
                     </TableRow>
                   ) : (
                     data?.[childDataKey]?.map((sub, i) => (
                       <TableRow key={i} className={classes.childRow}>
                         {subRowCols.map((col, j) => (
                           <TableCell
                             key={j}
                             align={col.align || "center"}
                             className={classes.childrenRowCell}
                           >
                             {col.render
                               ? col.render(sub)
                               : (sub[col.field] ?? "-")}
                           </TableCell>
                         ))}
                       </TableRow>
                     ))
                   )}
                 </TableBody>
               )}
             </Table>
           </TableContainer>
          </Box>
         </Collapse>
       </TableCell>
     </TableRow>
   );
 }

function ChildrenRow({ data, mainRowCols, subRowCols, subHeaderTitle, childDataKey, childApiCall, getChildData, mode, identifier_key, selected, setSelected, headerkey, isMobile, isExpanded, centerSubHeaders, unboundedNestedHeight }) {
  const classes = useStyles();
  const [open, setOpen] = useState(isExpanded || false);
  const [loadingChild, setLoadingChild] = useState(false);

  const hasChildren = childApiCall ? true : data?.[childDataKey]?.length > 0;

  async function onOpenClose() {
    const newOpen = !open;
    setOpen(newOpen);
    if (childApiCall && newOpen && !(childDataKey in data)) {
      setLoadingChild(true);
      await getChildData(data); 
      setLoadingChild(false);
    }
  }

    const onCheckboxClick = (e) => {
      e.stopPropagation();
      if (!setSelected) return;
      const val = e.target.checked;
      if (mode) {
        setSelected(val ? data[identifier_key] : null);
      }
    };

  if(isMobile){
    return(
      <MobileChildrenRow 
        data={data}
        mainRowCols={mainRowCols}
        subRowCols={subRowCols}
        subHeaderTitle={subHeaderTitle}
        childDataKey={childDataKey}
        childApiCall={childApiCall}
        getChildData={getChildData}
        mode={mode}
        identifier_key={identifier_key}
        selected={selected}
        setSelected={setSelected}
        headerkey={headerkey}
        hasChildren={hasChildren}
        onOpenClose={onOpenClose}
        loadingChild={loadingChild}
        onCheckboxClick={onCheckboxClick}
        open={open}
      />
    )
  }else {
    return (
      <>
        {/* Main Row */}
        <TableRow className={classes.mainRow}>
          {!_.isEmpty(mode) && (
            <TableCell className={classes.collaspseRowCell}>
              <CheckBox
                checked={data[identifier_key] === selected}
                onChange={onCheckboxClick}
              />
            </TableCell>
          )}
          <TableCell width="5%" className={classes.collaspseRowCell}>
            {hasChildren && (
              <IconButton size="small" onClick={onOpenClose}>
                {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
              </IconButton>
            )}
          </TableCell>
          {mainRowCols.map((col, idx) => (
            <TableCell
              key={idx}
              align={col.align || "center"}
              className={classes.mainRowCell}
            >
              {col.render ? col.render(data) : (data[col.field] ?? "-")}
            </TableCell>
          ))}
        </TableRow>

        {/* Collapsible Subtable */}
        {hasChildren && (
          <ChildTable
            mode={mode}
            subHeaderTitle={subHeaderTitle}
            subRowCols={subRowCols}
            data={data}
            childDataKey={childDataKey}
            mainRowCols={mainRowCols}
            open={open}
            loadingChild={loadingChild}
            setLoadingChild={setLoadingChild}
            centerSubHeaders={centerSubHeaders}
            unboundedNestedHeight={unboundedNestedHeight}
          />
        )}
      </>
    );
  }
}

export default function NestedTable({
  data = [],
  mainRowCols = [],
  subRowCols = [],
  subHeaderTitle = "",
  childDataKey = "",
  childApiCall = false,
  getChildData,
  rowSelection = {},
  paginationParams = {},
  isLoading = false,
  headerkey = "id",
  expandedRowKey = null,
  defaultExpandAll = false,
  arrowScroll = false,
  unboundedHeight = false,
  unboundedNestedHeight = false,
  centerSubHeaders = false,
  enableFilters = false,
  filterDataColumn,
  id,
}) {
  const classes = useStyles();
  const globalClasses = useGlobalStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const mode = rowSelection?.mode;
  const identifier_key = rowSelection?.identifier_key;
  const selected = rowSelection?.selected;
  const setSelected = rowSelection?.setSelected;
  const colSpanRows = mode === "single" ? mainRowCols.length + 2 : mainRowCols.length + 1;

  // --- arrowScroll: swap the native horizontal scrollbar for FiltersTable-style arrow buttons ---
  const desktopScrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const checkDesktopScrollable = () => {
    const el = desktopScrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  };

  // Scrolls the desktop table horizontally; wired to the arrowScroll left/right buttons below
  const scrollDesktopByX = (dir) => {
    const el = desktopScrollRef.current;
    if (el) el.scrollBy({ left: dir * 300, behavior: "smooth" });
  };

  useEffect(() => {
    if (!arrowScroll) return;
    const el = desktopScrollRef.current;
    if (!el) return;
    checkDesktopScrollable();
    el.addEventListener("scroll", checkDesktopScrollable);
    const ro = new ResizeObserver(checkDesktopScrollable);
    ro.observe(el);
    return () => {
      el.removeEventListener("scroll", checkDesktopScrollable);
      ro.disconnect();
    };
  }, [arrowScroll, data]);

  const initialFilterSettings = sessionStorage.getItem(`${id}.filters.${window.location.pathname}`);
  const storedFilters = id
    ? JSON.parse(sessionStorage.getItem(`${id}.filters.${window.location.pathname}`)) || []
    : initialFilterSettings;

  // --- filter state — seeded from sessionStorage (via id) or initialFilterSettings prop ---
  const [filterSettings, setFilterSettings] = useState(storedFilters);

  // Derived map for O(1) icon highlight lookups: field → searchText
  const columnFilters = useMemo(() => {
    const map = {};
    filterSettings?.forEach((f) => { map[f.searchedColumn] = f.searchText; });
    return map;
  }, [filterSettings]);

  // Apply a column filter — same as FiltersTable.handleSearch
  const applyColumnFilter = (col, value) => {
    const newEntry = {
      searchText: value,
      searchedColumn: col.field,
      searchedTitle: col.headerName || col.field,
      type: col.type,
    };
    const newSettings = filterSettings.some((f) => f.searchedColumn === col.field)
      ? filterSettings.map((f) => f.searchedColumn === col.field ? newEntry : f)
      : [...filterSettings, newEntry];
    setFilterSettings(newSettings);
    if (filterDataColumn && typeof filterDataColumn === 'function') filterDataColumn(newSettings);
  };

  // Remove a column filter by field — same as FiltersTable.clearFilterSettings
  const removeColumnFilter = (field) => {
    const newSettings = filterSettings.filter((f) => f.searchedColumn !== field);
    setFilterSettings(newSettings);
    if (filterDataColumn && typeof filterDataColumn === 'function') filterDataColumn(newSettings);
  };

  // Called by the internal FilterChip when a chip's × is clicked
  const clearFilterByIndex = (index) => {
    const newSettings = filterSettings.filter((_, i) => i !== index);
    setFilterSettings(newSettings);
    if (filterDataColumn && typeof filterDataColumn === 'function') filterDataColumn(newSettings);
  };

  // --- pagination (pageSizeOptions activates antd Pagination with size changer) ---
  const pageSizeOptions   = paginationParams?.pageSizeOptions;   // undefined → use CustomPagination
  const currentPageSize   = paginationParams?.pageLimit  || 10;
  const currentPage       = paginationParams?.page       || 1;
  const totalCount        = paginationParams?.totalCount || 0;
  const onPageChange      = paginationParams?.onPageChange;
  const onPageSizeChange  = paginationParams?.onPageSizeChange;

  return (
    <>
      {/* Filter chips — rendered internally when enableFilters=true, mirrors FiltersTable */}
      {enableFilters && (
        <FilterChip
          filterSettings={filterSettings}
          clearFilterSettings={clearFilterByIndex}
        />
      )}

      {isMobile ?
        <Box className={classes.mobileContainer}>
          {isLoading ? (
            <Box className={classes.mobileLoading}>
              <Loading />
            </Box>
          ) : data && data.length > 0 ? (
            data.map((row, i) => {
              const isExpanded = defaultExpandAll || expandedRowKey === row[identifier_key];
              return (
                <ChildrenRow
                  key={i}
                  data={row}
                  mainRowCols={mainRowCols}
                  subRowCols={subRowCols}
                  subHeaderTitle={subHeaderTitle}
                  childDataKey={childDataKey}
                  childApiCall={childApiCall}
                  getChildData={getChildData}
                  mode={mode}
                  identifier_key={identifier_key}
                  selected={selected}
                  setSelected={setSelected}
                  headerkey={headerkey}
                  isMobile={true}
                  isExpanded={isExpanded}
                />
              );
            })
          ) : (
            <Card className={classes.mobileNoDataCard}>
              <Box className={classes.emptyStateBox}>
                <Typography className={classes.emptyStateText}>
                  {commonStrings.noRecords}
                </Typography>
              </Box>
            </Card>
          )}
        </Box> :
        (() => {
          const desktopTable = (
            <Table className={classes.table}>
              <TableHead className={classes.header}>
                <TableRow>
                  {mode === "single" && <TableCell width="5" />}
                  <TableCell />
                  {mainRowCols.map((col, idx) => (
                    <TableCell key={idx} align={col.align || "center"} width={col.width || "auto"}>
                      <Box className={globalClasses.headerCellContent}>
                        {col.headerName}
                        {enableFilters && (
                          <ColumnFilterPopover
                            col={col}
                            activeValue={columnFilters[col.field] || ""}
                            onApply={applyColumnFilter}
                            onRemove={removeColumnFilter}
                          />
                        )}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>

              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={colSpanRows} className={classes.noData}>
                      <Loading />
                    </TableCell>
                  </TableRow>
                ) : data && data.length > 0 ? (
                  data.map((row, i) => {
                    const isExpanded = defaultExpandAll || expandedRowKey === row[identifier_key];
                    return (
                      <ChildrenRow
                        key={i}
                        data={row}
                        mainRowCols={mainRowCols}
                        subRowCols={subRowCols}
                        subHeaderTitle={subHeaderTitle}
                        childDataKey={childDataKey}
                        childApiCall={childApiCall}
                        getChildData={getChildData}
                        mode={mode}
                        identifier_key={identifier_key}
                        selected={selected}
                        setSelected={setSelected}
                        headerkey={headerkey}
                        isMobile={false}
                        isExpanded={isExpanded}
                        centerSubHeaders={centerSubHeaders}
                        unboundedNestedHeight={unboundedNestedHeight}
                      />
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={colSpanRows} className={classes.noData}>
                      {commonStrings.noRecords}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          );

          return arrowScroll ? (
            <Box className={classes.arrowScrollWrap}>
              {canScrollLeft && (
                <button type="button" className="ft-scroll-btn ft-scroll-btn-left" onClick={() => scrollDesktopByX(-1)}>
                  <LeftOutlined />
                </button>
              )}
              <TableContainer
                component={Paper}
                className={`${classes.root} ${classes.noHorizontalScrollbar} ${unboundedHeight ? classes.unboundedRoot : ""}`}
                ref={desktopScrollRef}
              >
                {desktopTable}
              </TableContainer>
              {canScrollRight && (
                <button type="button" className="ft-scroll-btn ft-scroll-btn-right" onClick={() => scrollDesktopByX(1)}>
                  <RightOutlined />
                </button>
              )}
            </Box>
          ) : (
            <TableContainer
              component={Paper}
              className={unboundedHeight ? `${classes.root} ${classes.unboundedRoot}` : classes.root}
            >
              {desktopTable}
            </TableContainer>
          );
        })()
      }

      {/* Pagination:
            • pageSizeOptions provided → antd Pagination (matches FiltersTable: size changer + page nav)
            • pageSizeOptions absent  → CustomPagination (backward compatible for existing consumers) */}
      {!_.isEmpty(paginationParams) && totalCount > 0 && (
        pageSizeOptions ? (
          <Box className={globalClasses.antPaginationWrapper}>
            <AntPagination
              current={currentPage}
              pageSize={currentPageSize}
              total={totalCount}
              pageSizeOptions={pageSizeOptions}
              showSizeChanger
              onChange={(newPage, newPageSize) => {
                if (Number(newPageSize) !== Number(currentPageSize)) {
                  // Size changed — antd resets page to 1 automatically;
                  // onPageSizeChange in the parent also resets page to 1
                  onPageSizeChange && onPageSizeChange(Number(newPageSize));
                } else {
                  onPageChange && onPageChange(newPage);
                }
              }}
            />
          </Box>
        ) : (
          <CustomPagination paginationParams={paginationParams} />
        )
      )}
    </>
  );
}